// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { utils } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import type { RecurringPlantingsHomeQuery } from '../../types/__generated__/graphql';
import { getID, getNumericID } from '../../utils';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import CropIdLink from '../crop/CropIdLink';
import { formatRecurringFreq } from '../recurringOrder';
import RecurringPlantingIdLink from './RecurringPlantingIdLink';
import RecurringPlantingOptions from './RecurringPlantingOptions';

type RecurringPlantingData = RecurringPlantingsHomeQuery['recurring_plantings']['edges'][number]['node'];
interface Props {
  readonly plantings: RecurringPlantingData[];
}

export default function RecurringPlantingTable({ plantings }: Props) {
  const { permissions } = useUserContext();
  const userCanEditRecurringPlantings = permissions.can_edit_plantings;
  const idValues: React.ReactElement[] = [];
  const cropValues: (React.ReactElement | string)[] = [];
  const startTimeValues: React.ReactElement[] = [];
  const endTimeValues: (React.ReactElement | string)[] = [];
  const frequencyValues: string[] = [];
  let shouldShowEndColumn = false;
  for (const planting of plantings) {
    const gotId = getID(planting.id);
    const title = planting.recurring_planting_name;
    idValues.push(<RecurringPlantingIdLink id={gotId} title={title} />);
    cropValues.push(
      planting.crop ? <CropIdLink id={getID(planting.crop.id)} name={planting.crop.crop_name} /> : 'Unknown',
    );
    frequencyValues.push(formatRecurringFreq(planting.recurring_options));
    startTimeValues.push(
      <VisibleAtContainer
        up={<Time time={planting.recurring_start_time} />}
        down={<Time time={planting.recurring_start_time} options={{ compactDisplay: true }} />}
      />,
    );
    endTimeValues.push(
      planting.recurring_end_time ? (
        <VisibleAtContainer
          up={<Time time={planting.recurring_end_time} />}
          down={<Time time={planting.recurring_end_time} options={{ compactDisplay: true }} />}
        />
      ) : (
        ''
      ),
    );
    if (planting.recurring_end_time) {
      shouldShowEndColumn = true;
    }
  }
  const columns: ColumnType[] = [
    {
      name: 'Name',
      values: idValues,
    },
    {
      name: 'Crop',
      values: cropValues,
    },
    {
      name: 'Frequency',
      values: frequencyValues,
    },
    {
      name: 'Start Date',
      smallName: 'Start',
      visibleAt: 'sm' as const,
      values: startTimeValues,
    },
    shouldShowEndColumn
      ? {
          name: 'End Date',
          smallName: 'End',
          values: endTimeValues,
        }
      : undefined,
    userCanEditRecurringPlantings
      ? {
          name: 'Actions',
          smallName: '',
          numeric: true,
          flex: '0 0 auto',
          values: plantings.map((planting) => (
            <RecurringPlantingOptions
              key={planting.id}
              onSuccess={() => {
                /** do nothing */
              }}
              useMoreIcon
              recurringPlantingId={getNumericID(planting.id)}
              status={planting.status}
            />
          )),
        }
      : undefined,
  ].filter(utils.notNull);

  return <Table columns={columns} />;
}
